import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  People,
  PollOutlined,
  Warning,
  CalendarToday,
} from "@mui/icons-material";
import { analyticsApi, NonVotersAnalytics } from "../services/analyticsApi";
import { toast } from "react-toastify";

const Dashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<NonVotersAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await analyticsApi.getNonVoters();
      setAnalytics(data);
    } catch (err: any) {
      console.error("Error loading analytics:", err);
      setError("Erreur lors du chargement des données");
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatLastVoteDate = (dateString: string | null) => {
    if (!dateString) return "Jamais voté";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Aucune donnée disponible</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontWeight: "bold",
          color: "primary.main",
        }}
      >
        Dashboard
      </Typography>

      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 3,
          mb: 4,
        }}
      >
        <Card
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                <People />
              </Avatar>
              <Typography variant="h6">Total Entraîneurs</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {analytics.totalCoaches}
            </Typography>
          </CardContent>
        </Card>

        {/* <Card
          sx={{
            background: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
            color: "white",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                <PollOutlined />
              </Avatar>
              <Typography variant="h6">Matchs du weekend</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {analytics.matchCount}
            </Typography>
          </CardContent>
        </Card> */}

        <Card
          sx={{
            background: "linear-gradient(135deg, #ed6c02 0%, #e65100 100%)",
            color: "white",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                <Warning />
              </Avatar>
              <Typography variant="h6">N'ont pas voté</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {analytics.coachesWhoDidntVote}
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: "linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)",
            color: "white",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                <CalendarToday />
              </Avatar>
              <Typography variant="h6">Weekend</Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              {formatDate(analytics.weekendDate)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Non-Voters Table */}
      {analytics.coachesWhoDidntVote > 0 && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "primary.main", fontWeight: "bold" }}
            >
              Entraîneurs qui n'ont pas voté ({analytics.coachesWhoDidntVote})
            </Typography>

            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Équipe</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Téléphone</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Dernier vote
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.coaches.map((coach, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "grey.50",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {coach.name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={coach.team}
                          size="small"
                          variant="outlined"
                          color={
                            coach.team === "No team assigned"
                              ? "default"
                              : "primary"
                          }
                        />
                      </TableCell>
                      <TableCell>{coach.number}</TableCell>
                      <TableCell>{coach.email}</TableCell>
                      <TableCell>
                        {formatLastVoteDate(coach.lastVoteDate)}
                      </TableCell>
                      <TableCell>
                        {coach.hasNeverVoted ? (
                          <Chip
                            label="Jamais voté"
                            size="small"
                            color="error"
                            variant="filled"
                          />
                        ) : (
                          <Chip
                            label={`${coach.daysSinceLastVote} jours`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {analytics.coachesWhoDidntVote === 0 && (
        <Card>
          <CardContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Excellent ! 🎉
              </Typography>
              <Typography>
                Tous les entraîneurs ont voté pour le weekend du{" "}
                {formatDate(analytics.weekendDate)}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Dashboard;
